import { ReactNode, useEffect, useRef, useState } from "react";
import {
  AccountInfo,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from "@azure/msal-browser";
import axios from "axios";
import { api, setTokenProvider, setUnauthorizedHandler } from "../api/client";
import { AuthContext, AuthUser } from "./AuthContext";
import { ActiveProvider, AuthConfig, fallbackAuthConfig, pickProvider } from "./msalConfig";

type Phase =
  | { kind: "loading"; text: string }
  | { kind: "error"; title: string; text: string }
  | { kind: "denied"; text: string; email?: string }
  | { kind: "ready"; user: AuthUser; canLogout: boolean };

async function loadAuthConfig(): Promise<AuthConfig> {
  try {
    return (await api.get<AuthConfig>("/auth/config")).data;
  } catch {
    return fallbackAuthConfig();
  }
}

export default function AuthGate({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<Phase>({ kind: "loading", text: "Aanmelden…" });
  const pcaRef = useRef<PublicClientApplication | null>(null);
  const accountRef = useRef<AccountInfo | null>(null);
  const providerRef = useRef<ActiveProvider | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchMe(canLogout: boolean) {
      try {
        const me = (await api.get<AuthUser>("/auth/me")).data;
        if (!cancelled) setPhase({ kind: "ready", user: me, canLogout });
      } catch (e) {
        if (cancelled) return;
        if (axios.isAxiosError(e) && e.response?.status === 403) {
          setPhase({
            kind: "denied",
            text: String(e.response.data?.detail ?? "Geen toegang."),
            email: accountRef.current?.username,
          });
          return;
        }
        setPhase({ kind: "error", title: "Authenticatie probleem", text: (e as Error)?.message ?? "Onbekende fout" });
      }
    }

    async function run() {
      const cfg = await loadAuthConfig();
      if (cfg.skip_auth) {
        await fetchMe(false);
        return;
      }
      const provider = pickProvider(cfg);
      if (!provider) {
        setPhase({
          kind: "error",
          title: "Login niet geconfigureerd",
          text: "De backend heeft geen B2C- of Entra-login geconfigureerd (AUTH_PROVIDER / B2C_CLIENT_ID).",
        });
        return;
      }
      providerRef.current = provider;
      const pca = new PublicClientApplication(provider.msal);
      pcaRef.current = pca;
      await pca.initialize();

      const redirectResult = await pca.handleRedirectPromise().catch((e: Error) => {
        // B2C: gebruiker annuleert / wachtwoord-vergeten-flow → gewoon opnieuw.
        console.warn("[auth] redirect-fout", e?.message);
        return null;
      });
      let account = redirectResult?.account ?? pca.getActiveAccount() ?? pca.getAllAccounts()[0] ?? null;
      if (!account) {
        setPhase({ kind: "loading", text: "Doorsturen naar KBS-login…" });
        await pca.loginRedirect({ scopes: provider.scopes });
        return; // pagina verlaat
      }
      pca.setActiveAccount(account);
      accountRef.current = account;

      setTokenProvider(async () => {
        const acc = accountRef.current!;
        try {
          const res = await pca.acquireTokenSilent({ scopes: provider.scopes, account: acc });
          return res.accessToken || res.idToken;
        } catch (e) {
          if (e instanceof InteractionRequiredAuthError) {
            await pca.acquireTokenRedirect({ scopes: provider.scopes, account: acc });
          }
          throw e;
        }
      });
      setUnauthorizedHandler(() => {
        // Token verlopen/ongeldig → opnieuw inloggen via redirect.
        pca.acquireTokenRedirect({ scopes: provider.scopes, account: accountRef.current ?? undefined });
      });

      await fetchMe(true);
    }

    run().catch((e: Error) => {
      if (!cancelled) setPhase({ kind: "error", title: "Authenticatie probleem", text: e?.message ?? "Onbekende fout" });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const logout = () => {
    const pca = pcaRef.current;
    setTokenProvider(null);
    if (pca) {
      pca.logoutRedirect({ account: accountRef.current ?? undefined });
    }
  };

  if (phase.kind === "loading") {
    return (
      <div className="flex h-screen items-center justify-center bg-kbs-light text-slate-500">
        {phase.text}
      </div>
    );
  }

  if (phase.kind === "error") {
    return (
      <Card title={phase.title}>
        <p className="mt-2 text-sm text-slate-600">{phase.text}</p>
      </Card>
    );
  }

  if (phase.kind === "denied") {
    return (
      <Card title="Geen toegang">
        <p className="mt-2 text-sm text-slate-600">{phase.text}</p>
        {phase.email ? <p className="mt-2 text-xs text-slate-500">Ingelogd als {phase.email}</p> : null}
        <button
          type="button"
          onClick={logout}
          className="mt-4 rounded bg-kbs-primary px-3 py-1.5 text-sm font-medium text-white"
        >
          Uitloggen / ander account
        </button>
      </Card>
    );
  }

  return (
    <AuthContext.Provider value={{ user: phase.user, canLogout: phase.canLogout, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex h-screen items-center justify-center bg-kbs-light text-kbs-primary">
      <div className="max-w-md rounded-lg bg-white p-8 shadow">
        <h2 className="text-lg font-bold">{title}</h2>
        {children}
      </div>
    </div>
  );
}
